/**
 * Demo access-gate client. The gate is a single shared password guarding
 * the whole demo (no username); a successful login sets an HttpOnly cookie
 * that keeps this browser logged in for ~7 days. These calls use plain
 * ``fetch`` (not the orval axios instance) so the gate's own 401s — e.g. a
 * wrong password — don't trip the global "session expired" handling.
 */

export interface AuthStatus {
  /** Whether the demo gate is active at all (false ⇒ no password configured). */
  enabled: boolean;
  /** Whether this browser currently holds a valid gate cookie. */
  authenticated: boolean;
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => null)) as
    | { data?: T; error?: string }
    | null;
  if (!res.ok) {
    throw new Error(body?.error || `Request failed (${res.status})`);
  }
  return body!.data as T;
}

/** Current gate state for this browser (is auth on, are we past it). */
export async function fetchAuthStatus(): Promise<AuthStatus> {
  const res = await fetch("/api/auth/me", { credentials: "include" });
  return unwrap<AuthStatus>(res);
}

/** Exchange the password for the gate cookie. Throws on a bad password. */
export async function login(password: string): Promise<AuthStatus> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  return unwrap<AuthStatus>(res);
}

/** Clear the gate cookie. */
export async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
}
